import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

// 创建认证上下文
const AuthContext = createContext();

// 认证提供者组件
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 初始化时检查用户是否已登录
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        // 验证 token 是否有效
        const isValid = await authService.validateToken();
        if (isValid) {
          const currentUser = authService.getCurrentUser();
          setUser(currentUser);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('认证初始化失败:', err);
        setError('认证初始化失败');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // 登录方法
  const login = async (identifier, password) => {
    setLoading(true);
    setError(null);
    try {
      const data = await authService.login(identifier, password);
      
      // 从本地存储获取最新的用户信息（可能包含了完整的头像数据）
      const currentUser = authService.getCurrentUser();
      
      // 使用最新的用户信息更新状态
      setUser(currentUser || data.user);
      return data;
    } catch (err) {
      console.error('登录失败:', err);
      setError(err.response?.data?.error?.message || '登录失败，请检查用户名和密码');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 注册方法
  const register = async (username, email, password) => {
    setLoading(true);
    setError(null);
    try {
      const data = await authService.register(username, email, password);
      setUser(data.user);
      return data;
    } catch (err) {
      console.error('注册失败:', err);
      setError(err.response?.data?.error?.message || '注册失败，请稍后重试');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 登出方法
  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // 更新用户头像
  const updateUserAvatar = async (fileId) => {
    try {
      const updatedUser = await authService.updateUserAvatar(fileId);
      if (updatedUser) {
        // 直接使用API返回的更新后的用户数据更新状态
        setUser(updatedUser);
      }
      return updatedUser;
    } catch (err) {
      console.error('更新头像失败:', err);
      throw err;
    }
  };

  // 更新密码
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      const data = await authService.updatePassword(currentPassword, newPassword);
      return data;
    } catch (err) {
      console.error('更新密码失败:', err);
      throw err;
    }
  };

  // 更新用户名
  const updateUsername = async (newUsername) => {
    try {
      const updatedUser = await authService.updateUsername(newUsername);
      if (updatedUser) {
        // 直接使用API返回的更新后的用户数据更新状态
        setUser(updatedUser);
      }
      return updatedUser;
    } catch (err) {
      console.error('更新用户名失败:', err);
      throw err;
    }
  };

  // 检查用户是否有关联的author身份
  const hasAuthorProfile = () => {
    return !!(user?.author);
  };

  // 获取用户的author信息
  const getAuthorInfo = () => {
    return user?.author || null;
  };

  // 检查用户是否有创作权限
  const hasCreatePermission = () => {
    return hasAuthorProfile();
  };

  // 提供给上下文的值
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    hasAuthorProfile: hasAuthorProfile(),
    authorInfo: getAuthorInfo(),
    hasCreatePermission: hasCreatePermission(),
    // 也可以提供函数形式让调用方动态获取最新状态
    checkAuthorProfile: hasAuthorProfile,
    getAuthorInfo: getAuthorInfo,
    checkCreatePermission: hasCreatePermission,
    login,
    register,
    logout,
    updatePassword,
    updateUserAvatar,
    updateUsername
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义 hook 以便于使用认证上下文
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用');
  }
  return context;
};

export default AuthContext;